import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RouteEnum } from '@app/constants/route.enum';
import { UtilityEnum } from '@app/constants/utility.enum';
import { RefreshTokenRes } from '@app/models/auth.model';
import { Auth } from '@app/services/auth/auth';
import { Utility } from '@app/services/utility/utility';
import { AppStore } from '@app/stores/app.store';
import { Observable, finalize, from, of, shareReplay, switchMap, throwError } from 'rxjs';

/** Opaque refresh tokens from ovino_node are 64-char hex (32 random bytes). */
const OPAQUE_REFRESH_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;

@Injectable({ providedIn: 'root' })
export class AuthRefreshCoordinator {
  private readonly authService = inject(Auth);
  private readonly utilityService = inject(Utility);
  private readonly appStore = inject(AppStore);
  private readonly router = inject(Router);

  private refreshInFlight: Observable<string> | null = null;

  isOpaqueRefreshToken(value: string | null | undefined): boolean {
    return typeof value === 'string' && OPAQUE_REFRESH_TOKEN_PATTERN.test(value.trim());
  }

  /** One shared refresh for all concurrent 401s — avoids token rotation races in DB. */
  refreshAccessToken(): Observable<string> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = from(this.loadValidRefreshToken()).pipe(
        switchMap((refreshToken) => {
          if (!refreshToken) {
            return throwError(() => new Error('No valid refresh token'));
          }
          return this.authService.refreshToken({ refreshToken });
        }),
        switchMap((res) => {
          if (!this.applyTokens(res)) {
            return throwError(() => new Error('Refresh response invalid'));
          }
          return of(res.data!.loginToken);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => {
          this.refreshInFlight = null;
        }),
      );
    }
    return this.refreshInFlight;
  }

  clearSession(): void {
    this.refreshInFlight = null;
    this.utilityService.deleteAllLocalStorage();
    this.appStore.reset();
    this.router.navigate([RouteEnum.LOADER]);
  }

  private async loadValidRefreshToken(): Promise<string | null> {
    const stored = await this.utilityService.getLocalStorageItem<string>(
      UtilityEnum.REFRESH_TOKEN_STORAGE_KEY,
    );
    if (!this.isOpaqueRefreshToken(stored)) {
      return null;
    }
    return stored!.trim();
  }

  private applyTokens(res: RefreshTokenRes): boolean {
    if (!res?.status || !res.data?.loginToken) {
      return false;
    }
    this.appStore.setAuthToken(res.data.loginToken);
    void this.utilityService.setLocalStorageItem(
      UtilityEnum.TOKEN_STORAGE_KEY,
      res.data.loginToken,
    );
    if (this.isOpaqueRefreshToken(res.data.refreshToken)) {
      void this.utilityService.setLocalStorageItem(
        UtilityEnum.REFRESH_TOKEN_STORAGE_KEY,
        res.data.refreshToken,
      );
    }
    return true;
  }
}

export function isUnauthorizedAuthError(error: HttpErrorResponse): boolean {
  const message = error?.error?.message;
  const code = error?.error?.code;
  return (
    error?.status === 401 &&
    (message === UtilityEnum.INVALID_TOKEN_ERR ||
      message === UtilityEnum.SESSION_IDLE_ERR ||
      code === UtilityEnum.SESSION_IDLE_CODE ||
      code === 'INVALID_TOKEN')
  );
}
